<template>
  <div class="ml-md-2">
    <v-layout>
      <v-flex
        shrink
        pt-1
        pr-0
        pb-0
      >
        <SiUserAvatar
          :uid="_user.uid"
          size="36"
          class="mt-2"
        />
        <SiFileUpload
          :save-path="`${appointmentPath}/attachments`"
          @update="fileUploadUpdate"
        >
          <v-btn
            slot="activator"
            icon
            class="mt-2"
          >
            <v-icon>attachment</v-icon>
          </v-btn>
        </SiFileUpload>
      </v-flex>
      <v-flex>
        <SiQuill
          v-model="newNote"
          :image-save-path="appointmentPath"
          rounded-corners-top
        />
      </v-flex>
    </v-layout>
    <v-layout
      justify-end
      mb-4
    >
      <v-flex
        class="py-0"
        shrink
      >
        <v-btn
          v-if="newNote"
          color="primary"
          depressed
          @click="saveNote"
        >
          Save Note
        </v-btn>
      </v-flex>
    </v-layout>
    <!-- <v-scale-transition group> -->
    <v-layout
      v-for="attachment in attachmentsList"
      :key="attachment.id"
      ma-0
      py-1
    >
      <SiUserAvatar
        :uid="attachment.uid"
        size="36"
        class="mt-1"
      />
      <v-flex
        py-0
        pl-2
      >
        <div class="">
          <span class="font-weight-bold mr-2">{{ _userName(attachment.uid) }}</span>
          <span class="caption grey--text">{{
            _formatDateTime(attachment.timestamp)
          }}</span>
          <v-menu
            v-if="!attachment.uploadTask"
            offset-y
          >
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                v-bind="attrs"
                class="ml-2"
                v-on="on"
              >
                <v-icon>more_horiz</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item @click="attachmentToDelete = attachment">
                <v-list-item-content>
                  <v-list-item-title>
                    <v-icon left>
                      delete
                    </v-icon> Delete
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
        <span
          v-if="attachment.attachmentType === 'note'"
          v-html="attachment.html"
        />
        <v-card 
          v-if="attachment.attachmentType === 'file'" 
          outlined
          class="mb-4" 
          @click="openFile(attachment)"
        >
          <v-progress-linear
            v-if="attachment.state === 'running'"
            :value="attachment.progress"
          />
          <v-layout
            ma-0
            pa-2
          >
            <v-icon left>
              description
            </v-icon>
            <div>
              {{ `${attachment.name}.${attachment.extension}` }}
              <div class="caption">
                {{ _fileSize(attachment.size) }}
              </div>
            </div>
          </v-layout>
        </v-card>
      </v-flex>
    </v-layout>
    <!-- </v-scale-transition> -->
    <SiDeleteButton
      :value="!!attachmentToDelete"
      title="Delete attachment?"
      text="Are you sure you want to delete this attachment."
      hide-activator
      @confirm-delete="deleteAttachment(attachmentToDelete)"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import helpers from '@/mixins/helpers'
import { orderBy } from 'lodash'

import binder from "@/mixins/binder";
import users from "@/mixins/users";
import { db, storage } from "@/plugins/firebase";

export default {
  mixins: [ helpers, binder, users ],
  props: {
    id: String,
    appointmentPath: String,
  },
  data: () => ({
    newNote: "",
    attachments: [],
    attachmentToDelete: null,
    uploadingFiles: {},
    loading: true,
  }),
  computed: {
    ...mapGetters(["_user"]),
    attachmentsList() {
      return orderBy(
        [
        ...this.attachments, 
        ...this._sortObject(this.uploadingFiles)
        ], 
        ['timestamp'], 
        ['desc']
        )
    },
    attachmentsPath() {
      return `${this.appointmentPath}/attachments`;
    },
  },
  watch: {
    appointmentPath(v) {
      if (v) {
        this.bind();
      } else {
        this.notes = {};
        this.newNote = "";
      }
    },
  },
  beforeMount() {
    this.bind();
  },
  methods: {
    bind() {
      this.$bindDb("attachments", this.attachmentsPath, { loader: "loading" });
    },
    saveNote() {
      function removeUnnecessaryBreaks(html) {
        const tempDiv = document.createElement("div");

        tempDiv.innerHTML = html;
        let nodes = tempDiv.childNodes;
        for (var nodeId = nodes.length - 1; nodeId >= 0; --nodeId) {
          if (!nodes[nodeId].innerText) {
            let childNodes = nodes[nodeId].childNodes;
            for (
              let childNodeId = childNodes.length - 1;
              childNodeId >= 0;
              --childNodeId
            ) {
              if (childNodes[childNodeId].tagName.toLowerCase() === "br") {
                nodes[nodeId].removeChild(childNodes[childNodeId]);
              }
            }
          }
        }
        return tempDiv.innerHTML;
      }

      let html = removeUnnecessaryBreaks(this.newNote);

      this.saveAttachment({ attachmentType: 'note', html })
      this.newNote = "";
    },
    fileUploadUpdate (file, details) {
      if (file.state === "complete") {
        this.saveAttachment({ ...details, attachmentType: 'file' })
        this.$delete(this.uploadingFiles, file.id)
      } else {
        this.$set(this.uploadingFiles, file.id, { ...file, attachmentType: 'file'} )
      }
    },
    saveAttachment (attachment) {
      db.collection(this.attachmentsPath).add({
            timestamp: new Date().toISOString(),
            uid: this._user.uid,
            ...attachment,
          },
        );
      },
    deleteAttachment(attachment) {
      this.attachmentToDelete = null

      db.doc(`${this.attachmentsPath}/${attachment.id}`).delete();
      
      if (attachment.attachmentType === 'file') storage.ref(attachment.storagePath).delete();
    },
    openFile (file) {
      const popup = window.open(
        file.downloadURL, 
        `${file.name}.${file.extension}`, 
        `height=${screen.height},width=900,location=no,menubar=yes,toolbar=yes`
        )
      const setTitle = () => {
        if(popup.document) { 
              popup.document.title = `${file.name}.${file.extension}`; 
          } else { 
              setTimeout(setTitle, 10); 
          }
      }
      setTitle()
    }
  },
};
</script>

<style lang="scss">
p:empty {
  margin: 0 !important;
}
</style>